<script lang="ts">
  import {apiVerifyScore} from 'Utility/investorFlow/investorFlowAPI';
  import {onMount, createEventDispatcher} from 'svelte';
  import {recaptchaToken, showRecaptchaV3} from 'Store/InvestorFlowStore';
  import Portal from 'Components/Common/PortalWrapper.svelte';
  import {embeddedWebComponent} from 'Store/embedded/embeddedFlowStore';

  type RecaptchaResponse = {
    isValid?: boolean;
  };

  export let recaptchaSiteKey: string;
  export let invisibleRecaptchaSiteKey: string;
  export let handleFormValidation: boolean = false;
  export let baseUrl: URL = undefined;
  /** This prop will be reassigned to the element that recaptcha v2 is attached to, use it to resize as needed. */
  export let recaptchaV2Box: HTMLElement = undefined;

  /**
   * Function to call when v3 token needs to be refreshed (i.e. when clicking a submit button)
   * It will resolve when the fresh token has been assigned to the store.
   *
   * Will resolve instantly if v3 is not currently enabled.
   */
  export const refreshV3 = (): Promise<void> => {
    return new Promise<void>(resolve => {
      if (!$showRecaptchaV3) return resolve();

      // Generating second token for sending it to backend
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      window.grecaptcha.ready(() => {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        window.grecaptcha
          .execute(invisibleRecaptchaSiteKey, {action: 'submit'})
          .then((token: string) => {
            disableFormSubmitButton();
            recaptchaToken.set(token);
            resolve();
          });
      });
    });
  };

  let recaptchaWrapper: HTMLElement;
  let recaptchaResponse: RecaptchaResponse = {isValid: false};
  let sizes = {width: 0, height: 0, yPadding: 0};
  const yPadding = $embeddedWebComponent ? 10 : 0;
  const dispatchEvent = createEventDispatcher();
  const enableFormSubmitButton = (): void => {
    if (recaptchaWrapper && handleFormValidation) {
      const form = recaptchaWrapper.closest('form');
      form.querySelector('[type="submit"]').removeAttribute('disabled');
    }
  };

  const disableFormSubmitButton = (): void => {
    if (recaptchaWrapper && handleFormValidation) {
      const form = recaptchaWrapper.closest('form');
      form.querySelector('[type="submit"]').setAttribute('disabled', '');
    }
  };

  onMount(() => {
    const recaptchaV3Script = document.createElement('script');
    recaptchaV3Script.src = `https://www.google.com/recaptcha/api.js?render=${invisibleRecaptchaSiteKey}`;
    recaptchaV3Script.async = true;

    document.body.appendChild(recaptchaV3Script);

    // Listening event when recaptcha v3 script is loaded
    recaptchaV3Script.onload = () => {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      window.grecaptcha.ready(() => {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        window.grecaptcha
          .execute(invisibleRecaptchaSiteKey, {action: 'homepage'})
          .then(async (token: string) => {
            recaptchaResponse = await apiVerifyScore(baseUrl, token);

            if (!recaptchaResponse.isValid) {
              $showRecaptchaV3 = false;
              recaptchaToken.set('');

              // failed v3 render v2
              // eslint-disable-next-line @typescript-eslint/ban-ts-comment
              // @ts-ignore
              window.grecaptcha.render('recaptcha-v2', {
                sitekey: recaptchaSiteKey,
                callback: onloadCallback,
                'expired-callback': handleCaptchaExpired,
              });
              sizes = {width: 304, height: 78, yPadding};
            } else {
              recaptchaToken.set(token);
              dispatchEvent('tokenLoaded');
            }
          });
      });
    };
  });

  // Since recaptcha component does not have its own form entity but it will be
  // part of many we need to ensure the triggering of any submit action.
  document.addEventListener('submit', async event => {
    if (handleFormValidation) {
      event.preventDefault();
      const form = event.target as HTMLFormElement;

      // Generating second token for sending it to backend
      await refreshV3();
      new FormData(form);
      form.submit();
      recaptchaToken.set('');
    }
  });

  document.addEventListener('formdata', event => {
    const formData = event.formData;
    formData.append('g-recaptcha-response', $recaptchaToken);

    if ($showRecaptchaV3) {
      formData.append(
        'g-recaptcha-invisible',
        JSON.stringify($showRecaptchaV3)
      );
    }
  });

  const onloadCallback = (e: string) => {
    recaptchaToken.set(e);
    dispatchEvent('tokenLoaded');
  };

  const handleCaptchaExpired = (): void => {
    recaptchaToken.set('');
  };

  window.onloadCallback = onloadCallback;
  window.recaptchaExpired = handleCaptchaExpired;

  $: if ($recaptchaToken) {
    enableFormSubmitButton();
  } else {
    disableFormSubmitButton();
  }
</script>

{#if $embeddedWebComponent}
  <Portal {sizes}>
    <div
      class="g-recaptcha-wrapper"
      data-testid="g-recaptcha-wrapper"
      id="g-recaptcha-wrapper"
      bind:this={recaptchaWrapper}
    >
      <div
        id="recaptcha-v2"
        data-testid="recaptcha-v2"
        class="g-recaptcha"
        bind:this={recaptchaV2Box}
      />
    </div>
  </Portal>
{:else}
  <div
    class="g-recaptcha-wrapper"
    data-testid="g-recaptcha-wrapper"
    id="g-recaptcha-wrapper"
    bind:this={recaptchaWrapper}
  >
    <div
      id="recaptcha-v2"
      data-testid="recaptcha-v2"
      class="g-recaptcha"
      bind:this={recaptchaV2Box}
    />
  </div>
{/if}

<style lang="postcss">
  .g-recaptcha {
    transform-origin: 0 0;

    /* Specific breakpoint for devices viewports under default recaptcha width 304px (for this page it reaches in 360)*/
    @media screen and (max-width: 360px) {
      width: 13.875rem;
    }

    @media screen and (min-width: 415px) and (max-width: 768px) {
      margin: 0 auto;
    }
  }
</style>
